<template>
  <div class="hms-page">
    <Navigation />
    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="m-0"><i class="mdi mdi-account-heart me-2"></i>Women's Health — Full Lifecycle</h4>
        </div>
      </div>

      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='preconception' }" @click="tab='preconception'; loadPcc()">
          <i class="mdi mdi-baby-bottle-outline"></i> Pre-conception
        </button>
        <button class="hms-tab" :class="{ active: tab==='ipv' }" @click="tab='ipv'; loadIpv()">
          <i class="mdi mdi-shield-account"></i> IPV Screening
          <span v-if="ipvStats.immediate_danger > 0" class="hms-pill hms-pill-danger ms-1">{{ ipvStats.immediate_danger }}</span>
        </button>
        <button class="hms-tab" :class="{ active: tab==='menopause' }" @click="tab='menopause'; loadMeno()">
          <i class="mdi mdi-leaf-maple"></i> Menopause
        </button>
        <button class="hms-tab" :class="{ active: tab==='survivorship' }" @click="tab='survivorship'; loadSurv()">
          <i class="mdi mdi-ribbon"></i> Cancer Survivorship
        </button>
      </div>

      <!-- ============ PRE-CONCEPTION ============ -->
      <div v-if="tab==='preconception'">
        <div class="d-flex justify-content-end mb-2">
          <button class="hms-btn hms-btn-blue" @click="openPccModal"><i class="mdi mdi-plus"></i> Enrol patient</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>Patient</th><th>Age</th><th>Horizon</th><th>HIV / Syph / RubImm</th><th>Chronic</th><th>Folic acid</th><th>Status</th><th></th></tr></thead>
          <tbody>
            <tr v-for="r in pccRows" :key="r.pcc_id">
              <td><strong>{{ r.patient_name }}</strong><div class="hms-muted-sm">{{ r.patient_no }}</div></td>
              <td>{{ r.age }}</td>
              <td>{{ (r.planning_horizon || '').replace(/_/g,' ') }}</td>
              <td class="hms-muted-sm">{{ r.hiv_status }} · {{ r.syphilis_status }} · {{ r.rubella_immune }}</td>
              <td>
                <span v-if="r.diabetes_present==1" class="hms-pill hms-pill-warn">DM</span>
                <span v-if="r.hypertension_present==1" class="hms-pill hms-pill-warn">HTN</span>
                <span v-if="r.epilepsy_present==1" class="hms-pill hms-pill-warn">epi</span>
                <span v-if="r.mental_health_concern==1" class="hms-pill hms-pill-warn">MH</span>
              </td>
              <td>
                <span v-if="r.folic_acid_started==1" class="hms-pill hms-pill-success">{{ r.folic_acid_dose }}</span>
                <span v-else class="hms-pill hms-pill-danger">not started</span>
              </td>
              <td><span class="hms-pill" :class="pccStatusPill(r.status)">{{ r.status.replace(/_/g,' ') }}</span></td>
              <td><button class="hms-btn hms-btn-secondary hms-btn-sm" @click="editPcc(r)"><i class="mdi mdi-pencil"></i></button></td>
            </tr>
            <tr v-if="!pccRows.length"><td colspan="8" class="text-center hms-pad hms-muted">No pre-conception clients enrolled.</td></tr>
          </tbody>
        </table>

        <div v-if="pccModal" class="hms-modal-bg" @click.self="pccModal=false">
          <div class="hms-modal">
            <div class="hms-modal-head"><h5><i class="mdi mdi-baby-bottle-outline"></i> Pre-conception enrolment</h5><button class="hms-btn hms-btn-secondary" @click="pccModal=false"><i class="mdi mdi-close"></i></button></div>
            <div class="hms-modal-body">
              <div class="hms-form-grid">
                <label>Patient ID <input type="number" v-model="pccForm.patient_id" class="hms-input" :disabled="!!pccForm.pcc_id" /></label>
                <label>Planning horizon
                  <select v-model="pccForm.planning_horizon" class="hms-input">
                    <option value="3_6_months">3-6 months</option>
                    <option value="6_12_months">6-12 months</option>
                    <option value="12_plus_months">12+ months</option>
                    <option value="active_trying">Actively trying</option>
                  </select>
                </label>
              </div>
              <h6 class="mt-3"><i class="mdi mdi-flask"></i> Baseline screens</h6>
              <div class="hms-form-grid">
                <label>HIV
                  <select v-model="pccForm.hiv_status" class="hms-input">
                    <option value="unknown">Unknown</option><option value="negative">Negative</option><option value="positive">Positive</option><option value="declined">Declined</option>
                  </select>
                </label>
                <label>Syphilis
                  <select v-model="pccForm.syphilis_status" class="hms-input">
                    <option value="unknown">Unknown</option><option value="negative">Negative</option><option value="positive">Positive</option>
                  </select>
                </label>
                <label>Rubella immunity
                  <select v-model="pccForm.rubella_immune" class="hms-input">
                    <option value="unknown">Unknown</option><option value="immune">Immune</option><option value="non_immune">Non-immune</option>
                  </select>
                </label>
                <label>Hb (g/dL) <input type="number" step="0.1" v-model="pccForm.hb_level" class="hms-input" /></label>
                <label>BMI <input type="number" step="0.1" v-model="pccForm.bmi" class="hms-input" /></label>
                <label>BMI category
                  <select v-model="pccForm.bmi_category" class="hms-input">
                    <option value="not_calculated">—</option>
                    <option value="underweight">Underweight</option>
                    <option value="normal">Normal</option>
                    <option value="overweight">Overweight</option>
                    <option value="obese_1">Obese class 1</option>
                    <option value="obese_2">Obese class 2</option>
                  </select>
                </label>
              </div>
              <h6 class="mt-3"><i class="mdi mdi-pill"></i> Chronic conditions</h6>
              <div class="hms-form-grid">
                <label class="hms-check"><input type="checkbox" v-model="pccForm.diabetes_present" :true-value="1" :false-value="0" /> Diabetes</label>
                <label>HbA1c <input type="number" step="0.01" v-model="pccForm.hba1c_value" class="hms-input" /></label>
                <label class="hms-check"><input type="checkbox" v-model="pccForm.hypertension_present" :true-value="1" :false-value="0" /> Hypertension</label>
                <label>BP <input v-model="bpDisplay" class="hms-input" placeholder="120/80" /></label>
                <label class="hms-check"><input type="checkbox" v-model="pccForm.epilepsy_present" :true-value="1" :false-value="0" /> Epilepsy</label>
                <label class="hms-check"><input type="checkbox" v-model="pccForm.mental_health_concern" :true-value="1" :false-value="0" /> Mental health concern</label>
              </div>
              <h6 class="mt-3"><i class="mdi mdi-leaf"></i> Folic acid</h6>
              <div class="hms-form-grid">
                <label class="hms-check"><input type="checkbox" v-model="pccForm.folic_acid_started" :true-value="1" :false-value="0" /> Folic acid started</label>
                <label>Dose
                  <select v-model="pccForm.folic_acid_dose" class="hms-input">
                    <option value="400mcg_standard">400 mcg standard</option>
                    <option value="5mg_high_risk">5 mg (high risk — prior NTD / diabetes / on AEDs)</option>
                  </select>
                </label>
                <label class="hms-check"><input type="checkbox" v-model="pccForm.high_risk_for_ntd" :true-value="1" :false-value="0" /> High risk for NTD</label>
              </div>
              <h6 class="mt-3"><i class="mdi mdi-account"></i> Behavioural + Partner</h6>
              <div class="hms-form-grid">
                <label>Smoking
                  <select v-model="pccForm.smoking" class="hms-input"><option value="never">Never</option><option value="former">Former</option><option value="current">Current</option></select>
                </label>
                <label>Alcohol
                  <select v-model="pccForm.alcohol" class="hms-input"><option value="none">None</option><option value="occasional">Occasional</option><option value="regular">Regular</option><option value="harmful">Harmful</option></select>
                </label>
                <label>Partner HIV
                  <select v-model="pccForm.partner_hiv_status" class="hms-input"><option value="unknown">Unknown</option><option value="negative">Negative</option><option value="positive">Positive</option></select>
                </label>
                <label class="hms-check"><input type="checkbox" v-model="pccForm.partner_tested_recently" :true-value="1" :false-value="0" /> Partner tested recently</label>
              </div>
              <label class="d-block mt-2">Notes <textarea v-model="pccForm.notes" rows="2" class="hms-input"></textarea></label>
            </div>
            <div class="hms-modal-foot">
              <button class="hms-btn hms-btn-secondary" @click="pccModal=false">Cancel</button>
              <button class="hms-btn hms-btn-success" :disabled="saving || !pccForm.patient_id" @click="savePcc">{{ saving ? 'Saving…' : 'Save' }}</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ IPV ============ -->
      <div v-if="tab==='ipv'">
        <div class="hms-confidential-banner">
          <i class="mdi mdi-shield-lock"></i> <strong>Confidential.</strong> WHO-recommended IPV inquiry. Always conduct in private. Disclosure triggers safety planning + referrals.
        </div>
        <div class="hms-grid-4 mb-3">
          <div class="hms-kpi"><div class="hms-kpi-lbl">Screened (12 mo)</div><div class="hms-kpi-num">{{ ipvStats.total || 0 }}</div></div>
          <div class="hms-kpi"><div class="hms-kpi-lbl">Disclosed</div><div class="hms-kpi-num">{{ ipvStats.positive || 0 }}</div></div>
          <div class="hms-kpi"><div class="hms-kpi-lbl hms-bad">Immediate danger</div><div class="hms-kpi-num hms-bad">{{ ipvStats.immediate_danger || 0 }}</div></div>
          <div class="hms-kpi"><div class="hms-kpi-lbl">Positive in pregnancy</div><div class="hms-kpi-num">{{ ipvStats.positive_in_pregnancy || 0 }}</div></div>
        </div>
        <div class="d-flex justify-content-end mb-2">
          <button class="hms-btn hms-btn-blue" @click="openIpvModal"><i class="mdi mdi-plus"></i> New screening</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>When</th><th>Patient</th><th>Context</th><th>Tool</th><th>Score</th><th>Disclosed</th><th>Severity</th><th>Referrals</th></tr></thead>
          <tbody>
            <tr v-for="r in ipvRows" :key="r.ipv_id" :class="ipvRowClass(r)">
              <td class="hms-muted-sm">{{ formatDateTime(r.screened_at) }}</td>
              <td><strong>{{ r.patient_name }}</strong><div class="hms-muted-sm">{{ r.patient_no }}</div></td>
              <td>{{ r.encounter_context }}</td>
              <td>{{ r.screening_tool }}</td>
              <td>{{ r.score_total || '—' }}</td>
              <td>
                <span v-if="r.disclosed_violence==1" class="hms-pill hms-pill-danger">YES</span>
                <span v-else class="hms-pill hms-pill-muted">no</span>
              </td>
              <td><span class="hms-pill" :class="ipvSeverityPill(r.severity)">{{ r.severity.replace(/_/g,' ') }}</span></td>
              <td class="hms-muted-sm">
                <span v-if="r.mental_health_referral==1">MH</span>
                <span v-if="r.legal_referral==1"> · legal</span>
                <span v-if="r.shelter_referral==1"> · shelter</span>
                <span v-if="r.police_report_made==1"> · police</span>
              </td>
            </tr>
            <tr v-if="!ipvRows.length"><td colspan="8" class="text-center hms-pad hms-muted">No IPV screenings recorded.</td></tr>
          </tbody>
        </table>

        <div v-if="ipvModal" class="hms-modal-bg" @click.self="ipvModal=false">
          <div class="hms-modal">
            <div class="hms-modal-head"><h5><i class="mdi mdi-shield-account"></i> IPV screening</h5><button class="hms-btn hms-btn-secondary" @click="ipvModal=false"><i class="mdi mdi-close"></i></button></div>
            <div class="hms-modal-body">
              <div class="hms-confidential-banner mb-3">In private. Saved confidentially.</div>
              <div class="hms-form-grid">
                <label>Patient ID <input type="number" v-model="ipvForm.patient_id" class="hms-input" /></label>
                <label>Encounter
                  <select v-model="ipvForm.encounter_context" class="hms-input">
                    <option value="opd">OPD</option><option value="anc">ANC</option><option value="pnc">PNC</option><option value="fp">FP</option><option value="admission">Admission</option><option value="sgbv_one_stop">SGBV centre</option>
                  </select>
                </label>
                <label>Tool
                  <select v-model="ipvForm.screening_tool" class="hms-input">
                    <option value="HITS">HITS</option><option value="HARK">HARK</option><option value="WAST">WAST</option><option value="AAS">AAS</option><option value="clinical_inquiry">Clinical inquiry</option>
                  </select>
                </label>
                <label class="hms-check"><input type="checkbox" v-model="ipvForm.screened_in_private" :true-value="1" :false-value="0" /> Done in private</label>
                <label>Score (HITS 4-20, ≥10 positive) <input type="number" v-model="ipvForm.score_total" class="hms-input" /></label>
                <label class="hms-check"><input type="checkbox" v-model="ipvForm.disclosed_violence" :true-value="1" :false-value="0" /> Violence disclosed</label>
              </div>
              <div v-if="ipvForm.disclosed_violence==1">
                <h6 class="mt-3"><i class="mdi mdi-alert"></i> Disclosure details</h6>
                <h6 style="font-size:12px; color:#4a5568">Types of violence</h6>
                <div class="hms-chip-row">
                  <label v-for="opt in violenceTypes" :key="opt" class="hms-chip">
                    <input type="checkbox" :value="opt" v-model="ipvViolenceArr" /> {{ opt.replace(/_/g,' ') }}
                  </label>
                </div>
                <div class="hms-form-grid mt-2">
                  <label>Perpetrator
                    <select v-model="ipvForm.perpetrator" class="hms-input">
                      <option value="current_partner">Current partner</option>
                      <option value="former_partner">Former partner</option>
                      <option value="family_member">Family member</option>
                      <option value="other">Other</option>
                      <option value="undisclosed">Undisclosed</option>
                    </select>
                  </label>
                  <label>Frequency
                    <select v-model="ipvForm.frequency" class="hms-input">
                      <option value="once">Once</option>
                      <option value="multiple_times">Multiple times</option>
                      <option value="ongoing">Ongoing</option>
                      <option value="escalating">Escalating</option>
                    </select>
                  </label>
                  <label>Severity
                    <select v-model="ipvForm.severity" class="hms-input">
                      <option value="">(auto)</option>
                      <option value="mild">Mild</option>
                      <option value="moderate">Moderate</option>
                      <option value="severe">Severe</option>
                      <option value="immediate_danger">Immediate danger</option>
                    </select>
                  </label>
                  <label>Danger Assessment score <input type="number" v-model="ipvForm.danger_assessment_score" class="hms-input" /></label>
                  <label class="hms-check"><input type="checkbox" v-model="ipvForm.children_at_risk" :true-value="1" :false-value="0" /> Children at risk</label>
                  <label class="hms-check"><input type="checkbox" v-model="ipvForm.pregnancy_at_screening" :true-value="1" :false-value="0" /> Pregnant at screening</label>
                </div>
                <h6 class="mt-3"><i class="mdi mdi-handshake"></i> Response + referrals</h6>
                <div class="hms-form-grid">
                  <label class="hms-check"><input type="checkbox" v-model="ipvForm.safety_planning_offered" :true-value="1" :false-value="0" /> Safety planning offered</label>
                  <label class="hms-check"><input type="checkbox" v-model="ipvForm.safety_planning_accepted" :true-value="1" :false-value="0" /> Safety plan accepted</label>
                  <label class="hms-check"><input type="checkbox" v-model="ipvForm.mental_health_referral" :true-value="1" :false-value="0" /> Mental-health referral</label>
                  <label class="hms-check"><input type="checkbox" v-model="ipvForm.legal_referral" :true-value="1" :false-value="0" /> Legal referral</label>
                  <label class="hms-check"><input type="checkbox" v-model="ipvForm.shelter_referral" :true-value="1" :false-value="0" /> Shelter referral</label>
                  <label class="hms-check"><input type="checkbox" v-model="ipvForm.police_report_made" :true-value="1" :false-value="0" /> Police report made</label>
                </div>
              </div>
              <label class="d-block mt-3">Notes (confidential) <textarea v-model="ipvForm.notes" rows="2" class="hms-input"></textarea></label>
            </div>
            <div class="hms-modal-foot">
              <button class="hms-btn hms-btn-secondary" @click="ipvModal=false">Cancel</button>
              <button class="hms-btn hms-btn-success" :disabled="saving || !ipvForm.patient_id" @click="saveIpv">{{ saving ? 'Saving…' : 'Save screening' }}</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ MENOPAUSE ============ -->
      <div v-if="tab==='menopause'">
        <div class="d-flex justify-content-end mb-2">
          <button class="hms-btn hms-btn-blue" @click="openMenoModal"><i class="mdi mdi-plus"></i> Register / Review</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>Patient</th><th>Age</th><th>Menstrual status</th><th>Symptoms</th><th>HRT</th><th>Bone</th><th>Next review</th><th></th></tr></thead>
          <tbody>
            <tr v-for="r in menoRows" :key="r.meno_id">
              <td><strong>{{ r.patient_name }}</strong><div class="hms-muted-sm">{{ r.patient_no }}</div></td>
              <td>{{ r.age }}</td>
              <td>{{ r.menstrual_status.replace(/_/g,' ') }}</td>
              <td class="hms-muted-sm">
                <span v-if="r.hot_flushes !== 'none'" class="hms-pill hms-pill-warn">flushes {{ r.hot_flushes }}</span>
                <span v-if="r.vaginal_dryness !== 'none'" class="hms-pill hms-pill-warn">GSM {{ r.vaginal_dryness }}</span>
                <span v-if="r.low_mood==1">mood</span>
              </td>
              <td>
                <span v-if="r.hrt_accepted==1" class="hms-pill hms-pill-success">{{ r.hrt_regimen || 'on HRT' }}</span>
                <span v-else-if="r.hrt_offered==1" class="hms-pill hms-pill-warn">offered</span>
                <span v-else class="hms-muted-sm">—</span>
              </td>
              <td class="hms-muted-sm">
                <span v-if="r.dexa_done==1">DEXA T {{ r.dexa_t_score }}</span>
                <span v-else>—</span>
              </td>
              <td>{{ formatDate(r.next_review_date) || '—' }}</td>
              <td><button class="hms-btn hms-btn-secondary hms-btn-sm" @click="editMeno(r)"><i class="mdi mdi-pencil"></i></button></td>
            </tr>
            <tr v-if="!menoRows.length"><td colspan="8" class="text-center hms-pad hms-muted">No menopause clients enrolled.</td></tr>
          </tbody>
        </table>

        <div v-if="menoModal" class="hms-modal-bg" @click.self="menoModal=false">
          <div class="hms-modal">
            <div class="hms-modal-head"><h5><i class="mdi mdi-leaf-maple"></i> Menopause clinic — record / review</h5><button class="hms-btn hms-btn-secondary" @click="menoModal=false"><i class="mdi mdi-close"></i></button></div>
            <div class="hms-modal-body">
              <div class="hms-form-grid">
                <label>Patient ID <input type="number" v-model="menoForm.patient_id" class="hms-input" :disabled="!!menoForm.meno_id" /></label>
                <label>Menstrual status
                  <select v-model="menoForm.menstrual_status" class="hms-input">
                    <option value="regular">Regular</option>
                    <option value="perimenopause">Perimenopause</option>
                    <option value="postmenopause">Postmenopause (≥12mo no period)</option>
                    <option value="surgical_menopause">Surgical menopause</option>
                    <option value="induced_chemo_radiation">Induced (chemo/radio)</option>
                    <option value="premature_ovarian_insufficiency">POI (&lt;40y)</option>
                  </select>
                </label>
                <label>Age at menopause <input type="number" v-model="menoForm.age_at_menopause" class="hms-input" /></label>
                <label>Months since last period <input type="number" v-model="menoForm.months_since_last_period" class="hms-input" /></label>
              </div>
              <h6 class="mt-3"><i class="mdi mdi-fire"></i> Symptoms</h6>
              <div class="hms-form-grid">
                <label>Hot flushes <select v-model="menoForm.hot_flushes" class="hms-input"><option v-for="o in severityOpts" :key="o">{{ o }}</option></select></label>
                <label>Night sweats <select v-model="menoForm.night_sweats" class="hms-input"><option v-for="o in severityOpts" :key="o">{{ o }}</option></select></label>
                <label>Vaginal dryness <select v-model="menoForm.vaginal_dryness" class="hms-input"><option v-for="o in severityOpts" :key="o">{{ o }}</option></select></label>
                <label>Dyspareunia <select v-model="menoForm.dyspareunia" class="hms-input"><option v-for="o in severityOpts" :key="o">{{ o }}</option></select></label>
                <label>Urinary symptoms <select v-model="menoForm.urinary_symptoms" class="hms-input"><option v-for="o in severityOpts" :key="o">{{ o }}</option></select></label>
                <label class="hms-check"><input type="checkbox" v-model="menoForm.low_mood" :true-value="1" :false-value="0" /> Low mood</label>
                <label class="hms-check"><input type="checkbox" v-model="menoForm.anxiety" :true-value="1" :false-value="0" /> Anxiety</label>
                <label class="hms-check"><input type="checkbox" v-model="menoForm.sleep_disturbance" :true-value="1" :false-value="0" /> Sleep disturbance</label>
              </div>
              <h6 class="mt-3"><i class="mdi mdi-bone"></i> Bone + CV risk</h6>
              <div class="hms-form-grid">
                <label class="hms-check"><input type="checkbox" v-model="menoForm.dexa_done" :true-value="1" :false-value="0" /> DEXA done</label>
                <label>T-score <input type="number" step="0.1" v-model="menoForm.dexa_t_score" class="hms-input" /></label>
                <label class="hms-check"><input type="checkbox" v-model="menoForm.fracture_history" :true-value="1" :false-value="0" /> Prior fracture</label>
                <label class="hms-check"><input type="checkbox" v-model="menoForm.calcium_vit_d_given" :true-value="1" :false-value="0" /> Calcium + Vit D given</label>
                <label>BP <input v-model="bpMenoDisplay" class="hms-input" placeholder="120/80" /></label>
                <label>Total cholesterol <input type="number" step="0.1" v-model="menoForm.total_cholesterol" class="hms-input" /></label>
                <label class="hms-check"><input type="checkbox" v-model="menoForm.family_history_cvd" :true-value="1" :false-value="0" /> Family hx CVD</label>
                <label>Smoking <select v-model="menoForm.smoking_status" class="hms-input"><option value="never">Never</option><option value="former">Former</option><option value="current">Current</option></select></label>
              </div>
              <h6 class="mt-3"><i class="mdi mdi-pill"></i> Treatment</h6>
              <div class="hms-form-grid">
                <label class="hms-check"><input type="checkbox" v-model="menoForm.hrt_offered" :true-value="1" :false-value="0" /> HRT offered</label>
                <label class="hms-check"><input type="checkbox" v-model="menoForm.hrt_accepted" :true-value="1" :false-value="0" /> HRT accepted</label>
                <label>HRT regimen <input v-model="menoForm.hrt_regimen" class="hms-input" placeholder="e.g. Estradiol 1mg + MPA 2.5mg" /></label>
                <label>HRT start date <input type="date" v-model="menoForm.hrt_start_date" class="hms-input" /></label>
                <label class="hms-check"><input type="checkbox" v-model="menoForm.local_estrogen_offered" :true-value="1" :false-value="0" /> Local estrogen offered</label>
                <label class="hms-check"><input type="checkbox" v-model="menoForm.ssri_offered" :true-value="1" :false-value="0" /> SSRI offered</label>
                <label class="hms-check"><input type="checkbox" v-model="menoForm.cbt_referral" :true-value="1" :false-value="0" /> CBT referral</label>
                <label>Next review <input type="date" v-model="menoForm.next_review_date" class="hms-input" /></label>
              </div>
              <label class="d-block mt-2">Notes <textarea v-model="menoForm.notes" rows="2" class="hms-input"></textarea></label>
            </div>
            <div class="hms-modal-foot">
              <button class="hms-btn hms-btn-secondary" @click="menoModal=false">Cancel</button>
              <button class="hms-btn hms-btn-success" :disabled="saving || !menoForm.patient_id" @click="saveMeno">{{ saving ? 'Saving…' : 'Save' }}</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ CANCER SURVIVORSHIP ============ -->
      <div v-if="tab==='survivorship'">
        <div class="d-flex justify-content-end mb-2">
          <button class="hms-btn hms-btn-blue" @click="openSurvModal"><i class="mdi mdi-plus"></i> Enrol survivor</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>Patient</th><th>Cancer</th><th>Stage</th><th>Treatment</th><th>Status</th><th>Progress</th><th>Next visit</th><th></th></tr></thead>
          <tbody>
            <tr v-for="r in survRows" :key="r.surv_id" :class="r.status==='recurrence_confirmed' ? 'row-action' : (r.status==='recurrence_suspected' ? 'row-warn' : '')">
              <td><strong>{{ r.patient_name }}</strong><div class="hms-muted-sm">{{ r.patient_no }}</div></td>
              <td>{{ r.cancer_type }}</td>
              <td>{{ r.stage_at_diagnosis || '—' }}</td>
              <td class="hms-muted-sm">{{ (r.treatment_modalities || '').replace(/,/g,' · ') }}</td>
              <td><span class="hms-pill" :class="survStatusPill(r.status)">{{ r.status.replace(/_/g,' ') }}</span></td>
              <td class="hms-muted-sm">{{ r.completed_visits }}/{{ r.total_visits }} visits</td>
              <td>{{ formatDate(r.next_visit_due) || '—' }}</td>
              <td><button class="hms-btn hms-btn-secondary hms-btn-sm" @click="viewSurv(r)"><i class="mdi mdi-eye"></i></button></td>
            </tr>
            <tr v-if="!survRows.length"><td colspan="8" class="text-center hms-pad hms-muted">No cancer survivors on surveillance.</td></tr>
          </tbody>
        </table>

        <div v-if="survModal" class="hms-modal-bg" @click.self="survModal=false">
          <div class="hms-modal">
            <div class="hms-modal-head"><h5><i class="mdi mdi-ribbon"></i> Enrol cancer survivor</h5><button class="hms-btn hms-btn-secondary" @click="survModal=false"><i class="mdi mdi-close"></i></button></div>
            <div class="hms-modal-body">
              <div class="hms-form-grid">
                <label>Patient ID <input type="number" v-model="survForm.patient_id" class="hms-input" /></label>
                <label>Cancer type
                  <select v-model="survForm.cancer_type" class="hms-input">
                    <option value="cervical">Cervical</option><option value="breast">Breast</option><option value="ovarian">Ovarian</option><option value="endometrial">Endometrial</option><option value="other">Other</option>
                  </select>
                </label>
                <label>Diagnosis date <input type="date" v-model="survForm.diagnosis_date" class="hms-input" /></label>
                <label>Stage at diagnosis <input v-model="survForm.stage_at_diagnosis" class="hms-input" placeholder="e.g. IIA" /></label>
                <label>Treatment start <input type="date" v-model="survForm.treatment_start" class="hms-input" /></label>
                <label>Treatment completion <input type="date" v-model="survForm.treatment_completion" class="hms-input" /></label>
                <label>Treating facility <input v-model="survForm.treating_facility" class="hms-input" /></label>
                <label>Protocol
                  <select v-model="survForm.surveillance_protocol" class="hms-input">
                    <option value="cervical_post_treatment">Cervical (6/12/18/24/36/48/60 mo)</option>
                    <option value="breast_post_treatment">Breast (3/6/9/12/18/24/36/48/60 mo)</option>
                  </select>
                </label>
              </div>
              <h6 class="mt-3">Treatment modalities</h6>
              <div class="hms-chip-row">
                <label v-for="opt in tmodalities" :key="opt" class="hms-chip">
                  <input type="checkbox" :value="opt" v-model="survTmArr" /> {{ opt.replace(/_/g,' ') }}
                </label>
              </div>
              <label class="d-block mt-2">Notes <textarea v-model="survForm.notes" rows="2" class="hms-input"></textarea></label>
            </div>
            <div class="hms-modal-foot">
              <button class="hms-btn hms-btn-secondary" @click="survModal=false">Cancel</button>
              <button class="hms-btn hms-btn-success" :disabled="saving || !survForm.patient_id" @click="saveSurv">{{ saving ? 'Saving…' : 'Save & schedule' }}</button>
            </div>
          </div>
        </div>

        <!-- Surveillance detail modal -->
        <div v-if="survDetailModal" class="hms-modal-bg" @click.self="survDetailModal=false">
          <div class="hms-modal" style="width:760px">
            <div class="hms-modal-head"><h5><i class="mdi mdi-calendar"></i> Surveillance schedule — {{ survDetail.case && survDetail.case.patient_name }}</h5><button class="hms-btn hms-btn-secondary" @click="survDetailModal=false"><i class="mdi mdi-close"></i></button></div>
            <div class="hms-modal-body">
              <table class="hms-table">
                <thead><tr><th>Visit</th><th>Scheduled</th><th>Status</th><th>Result</th><th>Completed</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="v in survDetail.visits" :key="v.csv_id" :class="v.examination_result==='confirmed_recurrence' ? 'row-action' : (v.examination_result==='suspicious' ? 'row-warn' : '')">
                    <td>{{ v.visit_timing.replace(/_/g,' ') }}</td>
                    <td>{{ formatDate(v.scheduled_date) }}</td>
                    <td><span class="hms-pill" :class="csvStatusPill(v.status)">{{ v.status }}</span></td>
                    <td><span v-if="v.examination_result !== 'pending'" class="hms-pill" :class="csvResultPill(v.examination_result)">{{ v.examination_result.replace(/_/g,' ') }}</span></td>
                    <td>{{ formatDate(v.completed_date) || '—' }}</td>
                    <td><button v-if="v.status==='scheduled'" class="hms-btn hms-btn-success hms-btn-sm" @click="openSurvVisitEntry(v)">Record</button></td>
                  </tr>
                </tbody>
              </table>
              <div v-if="survVisitEntry.csv_id" class="mt-3 p-3" style="background:#f7fafc; border-radius:8px;">
                <h6>Record surveillance visit</h6>
                <div class="hms-form-grid">
                  <label>Completed date <input type="date" v-model="survVisitEntry.completed_date" class="hms-input" /></label>
                  <label>Examination result
                    <select v-model="survVisitEntry.examination_result" class="hms-input">
                      <option value="no_recurrence">No recurrence ✓</option>
                      <option value="suspicious">Suspicious</option>
                      <option value="confirmed_recurrence">Confirmed recurrence</option>
                      <option value="inconclusive">Inconclusive</option>
                    </select>
                  </label>
                </div>
                <label class="d-block mt-2">Investigations done <input v-model="survVisitEntry.investigations_done" class="hms-input" /></label>
                <label class="d-block mt-2">Action taken <input v-model="survVisitEntry.action_taken" class="hms-input" /></label>
                <div class="mt-2 d-flex justify-content-end gap-2">
                  <button class="hms-btn hms-btn-secondary" @click="survVisitEntry={}">Cancel</button>
                  <button class="hms-btn hms-btn-success" @click="saveSurvVisit">Save result</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "WomensHealth",
  components: { Navigation },
  data() {
    return {
      tab: 'preconception', saving: false,
      // Pre-conception
      pccRows: [], pccModal: false, pccForm: this.blankPccForm(),
      // IPV
      ipvRows: [], ipvStats: {}, ipvModal: false, ipvForm: this.blankIpvForm(), ipvViolenceArr: [],
      violenceTypes: ['physical','sexual','emotional_psychological','economic_financial','controlling_behaviour','reproductive_coercion'],
      // Menopause
      menoRows: [], menoModal: false, menoForm: this.blankMenoForm(),
      severityOpts: ['none','mild','moderate','severe'],
      // Survivorship
      survRows: [], survModal: false, survForm: this.blankSurvForm(), survTmArr: [],
      tmodalities: ['cryotherapy','thermal_ablation','LEEP','cone_biopsy','simple_hysterectomy','radical_hysterectomy','lumpectomy','mastectomy','chemotherapy','radiotherapy','immunotherapy'],
      survDetail: { case: null, visits: [] }, survDetailModal: false, survVisitEntry: {}
    };
  },
  computed: {
    bpDisplay: {
      get() { return (this.pccForm.bp_systolic || '') + (this.pccForm.bp_diastolic ? '/' + this.pccForm.bp_diastolic : ''); },
      set(v) { const m = (v + '').match(/(\d+)\s*\/\s*(\d+)/); if (m) { this.pccForm.bp_systolic = m[1]; this.pccForm.bp_diastolic = m[2]; } }
    },
    bpMenoDisplay: {
      get() { return (this.menoForm.bp_systolic || '') + (this.menoForm.bp_diastolic ? '/' + this.menoForm.bp_diastolic : ''); },
      set(v) { const m = (v + '').match(/(\d+)\s*\/\s*(\d+)/); if (m) { this.menoForm.bp_systolic = m[1]; this.menoForm.bp_diastolic = m[2]; } }
    }
  },
  mounted() { this.loadPcc(); },
  methods: {
    blankPccForm() { return { pcc_id:null, patient_id:null, planning_horizon:'3_6_months',
      hiv_status:'unknown', syphilis_status:'unknown', hep_b_status:'unknown', rubella_immune:'unknown',
      hb_level:null, bmi:null, bmi_category:'not_calculated',
      diabetes_present:0, hba1c_value:null, hypertension_present:0, bp_systolic:null, bp_diastolic:null,
      epilepsy_present:0, mental_health_concern:0,
      folic_acid_started:0, folic_acid_dose:'400mcg_standard', high_risk_for_ntd:0,
      smoking:'never', alcohol:'none', partner_hiv_status:'unknown', partner_tested_recently:0,
      notes:'' }; },
    blankIpvForm() { return { patient_id:null, encounter_context:'opd', screening_tool:'HITS',
      screened_in_private:1, score_total:null, disclosed_violence:0,
      perpetrator:'not_applicable', frequency:'not_applicable', severity:'',
      danger_assessment_score:null, children_at_risk:0, pregnancy_at_screening:0,
      safety_planning_offered:0, safety_planning_accepted:0,
      mental_health_referral:0, legal_referral:0, shelter_referral:0, police_report_made:0,
      notes:'' }; },
    blankMenoForm() { return { meno_id:null, patient_id:null, menstrual_status:'regular',
      age_at_menopause:null, months_since_last_period:null,
      hot_flushes:'none', night_sweats:'none', vaginal_dryness:'none', dyspareunia:'none', urinary_symptoms:'none',
      low_mood:0, anxiety:0, sleep_disturbance:0, cognitive_concerns:0,
      dexa_done:0, dexa_t_score:null, fracture_history:0, calcium_vit_d_given:0,
      bp_systolic:null, bp_diastolic:null, total_cholesterol:null, family_history_cvd:0, smoking_status:'never',
      mammography_up_to_date:0, cervical_screen_up_to_date:0,
      hrt_offered:0, hrt_accepted:0, hrt_regimen:'', hrt_start_date:'',
      local_estrogen_offered:0, ssri_offered:0, gabapentin_offered:0, lifestyle_advice_given:1, cbt_referral:0,
      next_review_date:'', notes:'' }; },
    blankSurvForm() { return { patient_id:null, cancer_type:'cervical', diagnosis_date:'',
      stage_at_diagnosis:'', treatment_start:'', treatment_completion:new Date().toISOString().substring(0,10),
      treating_facility:'', surveillance_protocol:'cervical_post_treatment', notes:'' }; },

    // PCC
    async loadPcc() {
      const r = await executeJson({ function:'getPreconceptionList' }, hms);
      if (r.data && r.data.success) this.pccRows = r.data.data.rows || [];
    },
    openPccModal() { this.pccForm = this.blankPccForm(); this.pccModal = true; },
    editPcc(r) { this.pccForm = { ...r }; this.pccModal = true; },
    async savePcc() {
      this.saving = true;
      try {
        const r = await executeJson({ function:'savePreconceptionCare', ...this.pccForm }, hms);
        if (r.data && r.data.success) { this.pccModal = false; await this.loadPcc(); }
        else if (r.data) alert(r.data.message || 'Failed.');
      } finally { this.saving = false; }
    },
    pccStatusPill(s) { return ({ active:'hms-pill-warn', pregnancy_achieved:'hms-pill-success', transferred_to_anc:'hms-pill-info', withdrew:'hms-pill-muted', LTFU:'hms-pill-danger' })[s] || 'hms-pill-muted'; },

    // IPV
    async loadIpv() {
      const r = await executeJson({ function:'getIpvScreenings' }, hms);
      if (r.data && r.data.success) { this.ipvRows = r.data.data.rows || []; this.ipvStats = r.data.data.stats || {}; }
    },
    openIpvModal() { this.ipvForm = this.blankIpvForm(); this.ipvViolenceArr = []; this.ipvModal = true; },
    async saveIpv() {
      this.saving = true;
      try {
        const r = await executeJson({ function:'recordIpvScreening', ...this.ipvForm,
          violence_types: this.ipvViolenceArr.length ? this.ipvViolenceArr.join(',') : 'none' }, hms);
        if (r.data && r.data.success) { this.ipvModal = false; await this.loadIpv(); }
        else if (r.data) alert(r.data.message || 'Failed.');
      } finally { this.saving = false; }
    },
    ipvSeverityPill(s) { return ({ no_disclosure:'hms-pill-muted', mild:'hms-pill-warn', moderate:'hms-pill-warn', severe:'hms-pill-danger', immediate_danger:'hms-pill-danger' })[s] || 'hms-pill-muted'; },
    ipvRowClass(r) { if (r.severity === 'immediate_danger' || r.severity === 'severe') return 'row-action'; if (r.disclosed_violence == 1) return 'row-warn'; return ''; },

    // Menopause
    async loadMeno() {
      const r = await executeJson({ function:'getMenopauseList' }, hms);
      if (r.data && r.data.success) this.menoRows = r.data.data.rows || [];
    },
    openMenoModal() { this.menoForm = this.blankMenoForm(); this.menoModal = true; },
    editMeno(r) { this.menoForm = { ...r }; this.menoModal = true; },
    async saveMeno() {
      this.saving = true;
      try {
        const r = await executeJson({ function:'saveMenopauseClient', ...this.menoForm }, hms);
        if (r.data && r.data.success) { this.menoModal = false; await this.loadMeno(); }
        else if (r.data) alert(r.data.message || 'Failed.');
      } finally { this.saving = false; }
    },

    // Survivorship
    async loadSurv() {
      const r = await executeJson({ function:'getCancerSurveillanceList' }, hms);
      if (r.data && r.data.success) this.survRows = r.data.data.rows || [];
    },
    openSurvModal() { this.survForm = this.blankSurvForm(); this.survTmArr = []; this.survModal = true; },
    async saveSurv() {
      this.saving = true;
      try {
        const r = await executeJson({ function:'enrolCancerSurveillance', ...this.survForm,
          treatment_modalities: this.survTmArr.length ? this.survTmArr.join(',') : 'none' }, hms);
        if (r.data && r.data.success) { this.survModal = false; await this.loadSurv(); }
        else if (r.data) alert(r.data.message || 'Failed.');
      } finally { this.saving = false; }
    },
    async viewSurv(r) {
      const res = await executeJson({ function:'getCancerSurveillanceDetail', surv_id: r.surv_id }, hms);
      if (res.data && res.data.success) { this.survDetail = res.data.data; this.survDetailModal = true; this.survVisitEntry = {}; }
    },
    openSurvVisitEntry(v) { this.survVisitEntry = { csv_id: v.csv_id, completed_date: new Date().toISOString().substring(0,10), examination_result:'no_recurrence', investigations_done:'', action_taken:'' }; },
    async saveSurvVisit() {
      const r = await executeJson({ function:'recordCancerSurveillanceVisit', ...this.survVisitEntry }, hms);
      if (r.data && r.data.success) { this.survVisitEntry = {}; await this.viewSurv(this.survDetail.case); await this.loadSurv(); }
      else if (r.data) alert(r.data.message || 'Failed.');
    },
    survStatusPill(s) { return ({ on_surveillance:'hms-pill-info', recurrence_suspected:'hms-pill-warn', recurrence_confirmed:'hms-pill-danger', disease_free_5yr:'hms-pill-success', LTFU:'hms-pill-danger', transferred:'hms-pill-muted', died:'hms-pill-danger' })[s] || 'hms-pill-muted'; },
    csvStatusPill(s) { return ({ scheduled:'hms-pill-info', completed:'hms-pill-success', missed:'hms-pill-warn', defaulted:'hms-pill-danger' })[s] || 'hms-pill-muted'; },
    csvResultPill(r) { return ({ no_recurrence:'hms-pill-success', suspicious:'hms-pill-warn', confirmed_recurrence:'hms-pill-danger', inconclusive:'hms-pill-warn', pending:'hms-pill-muted' })[r] || 'hms-pill-muted'; },

    formatDate(d) { if (!d) return ''; const dt = new Date((d+'').replace(' ','T')); return isNaN(dt) ? d : dt.toLocaleDateString(); },
    formatDateTime(d) { if (!d) return ''; const dt = new Date((d+'').replace(' ','T')); return isNaN(dt) ? d : dt.toLocaleString(); }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 14px; flex-wrap: wrap; }
.hms-tab { padding: 10px 14px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 13px; display: inline-flex; align-items: center; gap: 6px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }
.hms-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.hms-table th { background: #f7fafc; padding: 8px; font-weight: 600; color: #4a5568; text-align: left; border-bottom: 1px solid #e2e8f0; }
.hms-table td { padding: 6px 8px; border-bottom: 1px solid #edf2f7; vertical-align: top; }
.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; text-transform: capitalize; margin-right: 2px; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }
.hms-muted, .hms-muted-sm { color: #a0aec0; } .hms-muted-sm { font-size: 11px; }
.hms-pad { padding: 16px; } .hms-bad { color: #742a2a; }
.row-warn { background: #fffaf0; } .row-action { background: #fff5f5; }

.hms-confidential-banner { background: #fef2f2; border-left: 4px solid #b91c1c; color: #7f1d1d; padding: 10px 14px; border-radius: 6px; margin-bottom: 12px; font-size: 13px; display: flex; align-items: center; gap: 8px; }

.hms-grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
@media (max-width: 900px) { .hms-grid-4 { grid-template-columns: 1fr 1fr; } }
.hms-kpi { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; }
.hms-kpi-lbl { font-size: 11px; color: #718096; font-weight: 600; text-transform: uppercase; }
.hms-kpi-num { font-size: 26px; font-weight: 700; color: #2d3748; }

.hms-input { width: 100%; padding: 7px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.hms-form-grid label { display: block; font-size: 12px; color: #4a5568; font-weight: 600; }
.hms-check { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; }
.hms-chip-row { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-chip { display: inline-flex; align-items: center; gap: 4px; background: #f7fafc; border: 1px solid #e2e8f0; padding: 4px 10px; border-radius: 14px; font-size: 12px; cursor: pointer; }
.hms-chip:has(input:checked) { background: #fed7d7; border-color: #fc8181; color: #742a2a; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue { background: #2b6cb0; color: #fff; }
.hms-btn-success { background: #38a169; color: #fff; }
.hms-btn-secondary { background: #edf2f7; color: #2d3748; border: 1px solid #cbd5e0; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.55); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; width: 700px; display: flex; flex-direction: column; overflow: hidden; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
</style>
